import {useUserStore} from '@/app/shared/state/UserDataModule';
import {EUserRole} from '@/app/shared/state/user-role';
import { useCodeListStore } from '@/app/shared/state/CodeListModule';
import { mriInvoices } from '@/app/mri/mri-routes';
import { settings } from '@/app/settings/settings-routes';
import {klipIndex} from '@/router/klip-index';
import { Prepaid } from '@/app/prepaid/prepaid-routes';
import { PaymentTypes } from '@/api/klip-api.proxy';
import {computed, defineComponent, ref} from 'vue';
import {useKlipApiProxy} from '@/plugins/proxy-client';
import {useRouter} from '@/router/router-vue3';
import {useFormatting} from "@/composables/formatting";
import { USE_BUSINESS_CENTRAL, useFeatureToggleStore } from '@/stores/feature-toggle-store';

export default defineComponent({
    compatConfig: {
        MODE: 3,
    },
    name: 'KlProfileDropdown',
    props: {},
    setup(props) {
        const router = useRouter();
        const {formatCurrency} = useFormatting();

        const isOpen = ref<boolean>(false);
        const toggleFocus = ref<boolean>(false);
        const prepaidSaldo = ref<number>(null);
        const isFinancialServiceAvailable = ref<boolean>(true);
        const userStore = useUserStore();
        const featureToggleStore = useFeatureToggleStore();

        const saldoLabel = computed((): string =>
            featureToggleStore.getFeatureToggle(USE_BUSINESS_CENTRAL) ? 'KLIP-saldo' : 'Uw saldo');

        const isOnlyPdaManager = computed<boolean>(() => {
            if (user.value.roles.length > 1) {
                return false;
            }
           return userStore.hasRole([EUserRole.pda_manager]);
        });

        const user = computed(() => {
            return userStore.user;
        });

        const userDataScopes = computed(() => {
            return userStore.scopes;
        });

        const userDataScope = computed(() => {
            return userStore.scope;
        });

        const financialServiceAvailable = computed(() => {
            return isFinancialServiceAvailable.value;
        });

        const organisationName = computed(() => {
            let name = user.value.organisation;
            if (userStore.hasRole([EUserRole.una_manager, EUserRole.una_operator, EUserRole.una_reader]) &&
                user.value.displayName
            ) {
                name = user.value.displayName;
            }
            return name;
        });

        const routeMriInvoices = computed(() => {
            return mriInvoices;
        });

        const routeSettings = computed(() => {
            return settings;
        });

        const routeKlipIndex = computed(() => {
            return klipIndex;
        });

        const routePrepaid = computed(() => {
            return Prepaid;
        });

        const userScope = computed(() => {
            if(userStore.scopes.length === 0 && userStore.hasRole([EUserRole.org_manager])) {
                return {
                    key: 'org',
                    abbr: 'ORG',
                    label: 'Organisatie Beheerder'
                }
            }
            return userStore.scopes.find((scope) => userStore.scope === scope.key);
        });

        const userHasMultipleRoles = computed(() => {
            return Object.keys(userStore.scopes).length > 1;
        });

        const showRolepicker = computed(() => {
            return userHasMultipleRoles.value;
        });

        const rolePickerItems = computed(() => {
            return user.value.roles.map((role) => {
                return {
                    key: role,
                    label: useCodeListStore().userRoleCodes(role),
                };
            });
        });

        const showSettings = computed(() => {
            //hide settings if the user has only the map request reader role.
            if (user.value.roles.length > 1) {
                return true;
            }
            return !userStore.hasRole([EUserRole.mrr]);
        });

        const showBalance = computed(() => {
            return userStore.paymentType !== PaymentTypes.Invoice && !userStore.user.isCitizen && userStore.scope === 'mri' && userStore.hasRole([EUserRole.mri]);
        });

        const hasPrepaidSaldo = computed(() => {
            return prepaidSaldo.value != null;
        });


        const close = () => {
            isOpen.value = false;
        }

        const toggle = () => {
            isOpen.value = !isOpen.value;
            if (isOpen.value && showBalance.value) {
                useKlipApiProxy().payment_GetPrepaidAmount()
                    .then((response) => {
                        isFinancialServiceAvailable.value = response.result.financialServiceAvailable;
                        prepaidSaldo.value = response.result.amount;
                    });
            }
        }

        const switchScope = (scope: string) => {
            userStore.setScope(scope);
            close();
            router.push('/' + scope);
        }

        return {
            isOpen,
            toggleFocus,
            prepaidSaldo,
            isOnlyPdaManager,
            user,
            userDataScopes,
            userDataScope,
            organisationName,
            routeMriInvoices,
            routeSettings,
            routeKlipIndex,
            routePrepaid,
            userScope,
            userHasMultipleRoles,
            showRolepicker,
            rolePickerItems,
            showSettings,
            showBalance,
            hasPrepaidSaldo,
            financialServiceAvailable,
            saldoLabel,
            formatCurrency,
            close,
            toggle,
            switchScope,
        }
    }
})
